import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/brand";
import Magnetic from "./premium/Magnetic";

export default function Header({ onCta }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      style={{ top: "var(--df-announce-h, 0px)" }}
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-[#0F1B3D]/10"
          : "bg-white/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between md:grid md:grid-cols-[1fr_auto_1fr]">
        <a href="#top" data-testid="header-logo-link" className="flex items-center md:justify-self-start">
          <span className="block h-[68px] w-[170px] rounded-xl overflow-hidden">
            <img
              src={BRAND.logoUrl}
              alt="DriveFlow Technologies"
              width="680"
              height="355"
              decoding="async"
              className="h-full w-full object-cover scale-110"
            />
          </span>
        </a>

        <nav className="hidden md:flex items-center justify-center gap-5 lg:gap-8 xl:gap-10 px-3">
          <a
            href="/taxi-conventionne"
            data-testid="nav-taxi-cpam"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F1B3D]/80 hover:text-[#0F1B3D] transition-colors whitespace-nowrap"
          >
            Taxi CPAM
            <span className="h-1.5 w-1.5 rounded-full bg-[#4D8DFF]" />
          </a>
          <a
            href="/web"
            data-testid="nav-web"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F1B3D]/80 hover:text-[#0F1B3D] transition-colors whitespace-nowrap"
          >
            Web
            <span className="h-1.5 w-1.5 rounded-full bg-[#007AFF]" />
          </a>
          <a
            href="/blog"
            data-testid="nav-blog"
            className="text-sm font-medium text-[#0F1B3D]/80 hover:text-[#0F1B3D] transition-colors whitespace-nowrap"
          >
            Blog
          </a>
          <a
            href="https://app.mydriveflow.fr"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-client-area"
            className="text-sm font-medium text-[#0F1B3D]/80 hover:text-[#0F1B3D] transition-colors whitespace-nowrap"
          >
            Espace client
          </a>
        </nav>

        <div className="flex items-center gap-3 md:justify-self-end">
          <div className="hidden md:block">
            <Magnetic strength={0.25}>
              <button
                onClick={onCta}
                data-testid="header-cta-trial"
                className="bg-[#0F1B3D] text-white hover:bg-black transition-all duration-300 px-5 py-3 rounded-full text-sm font-semibold shadow-lg shadow-[#0F1B3D]/20 whitespace-nowrap"
              >
                Essai gratuit 30 jours
              </button>
            </Magnetic>
          </div>
          <button
            className="p-2 text-[#0F1B3D]"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="bg-white border-t border-[#0F1B3D]/10" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 space-y-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={`/#${l.id}`}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.id}`}
                className="block text-base font-medium text-[#0F1B3D]/80 hover:text-[#0F1B3D] transition-colors"
              >
                {l.label}
              </a>
            ))}
            <a
              href="/taxi-conventionne"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-taxi-cpam"
              className="lg:hidden inline-flex items-center gap-1.5 text-base font-medium text-[#0F1B3D]/80"
            >
              Taxi CPAM
              <span className="h-1.5 w-1.5 rounded-full bg-[#4D8DFF]" />
            </a>
            <a
              href="/web"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-web"
              className="md:hidden flex items-center gap-1.5 text-base font-medium text-[#0F1B3D]/80"
            >
              Web
              <span className="h-1.5 w-1.5 rounded-full bg-[#007AFF]" />
            </a>
            <a
              href="/blog"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-blog"
              className="md:hidden block text-base font-medium text-[#0F1B3D]/80"
            >
              Blog
            </a>
            <a
              href="https://app.mydriveflow.fr"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="mobile-client-area"
              className="md:hidden block text-base font-medium text-[#0F1B3D]/80"
            >
              Espace client
            </a>
            <button
              onClick={() => {
                setOpen(false);
                onCta();
              }}
              data-testid="mobile-cta-trial"
              className="md:hidden w-full bg-[#0F1B3D] text-white px-5 py-3 rounded-full text-sm font-semibold"
            >
              Essai gratuit 30 jours
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
